<template>
  <div class="relative w-full h-full">
    <DeckSiteSlide
      :slide="slide"
      :site="site"
      :user="userState.user"
      :is-admin="true"
      :camera-id="cameraId"
      :slide-refresh-key="siteEditorRefreshKey"
      :hide-slide-title="true"
      :hidden-hotspot-ids="hiddenHotspotIds"
      :clicked-hotspot="clickedHotspot"
      :three-d-viewer="threeDViewer"
      :is-editor-mode="true"
      @camera-ready="$emit('camera-ready', $event)"
      @set-camera="initCameraParams"
      @site-data-change="$emit('update:siteData', $event)"
      @camera-change="updateCamera"
      @select-hotspot="selectLayer"
      @initialized="initialized = true"
      @interaction="onInteraction"
      @contextmenu="openHotspotListContextMenu"
    >
      <template #top-bar>
        <div class="absolute top-0 left-0 right-0 z-20">
          <EditorTitleOverlay>
            <ChevronLeftIcon v-if="isEditing" class="w-5 h-5 mr-auto cursor-pointer" data-testid="back-btn" @click="onLeave" />
            <div class="flex flex-col items-center justify-center flex-grow text-2xl drop-shadow max-w-4/5">
              <InlineEditable
                :key="isNew.toString()"
                ref="slideTitleRef"
                :value="slideTitle"
                class="max-w-full whitespace-nowrap"
                theme="dark"
                placeholder="Slide title"
                :focus-on-mount="isNew"
                @save="saveTitle"
                @cancel="slideTitle = slide.title"
                @clear="slideTitle = slide.title"
                @input="slideTitle = $event"
              />
            </div>
            <div v-if="!isEditing" class="flex flex-none ml-auto">
              <VButton class="mr-4" size="xs" data-testid="edit-360-btn" @click="$emit('start-edit')">
                <CubeTransparentIcon class="inline-block w-4 h-4 mr-1" />
                Edit Slide
              </VButton>
              <VButton key="swap" size="xs" data-testid="edit-site-btn" @click="goToSite">
                <CubeIcon class="inline-block w-4 h-4 mr-1" />
                Go to Project
              </VButton>
            </div>
            <div v-if="isNew && isEditing" class="flex flex-none ml-auto gap-2">
              <slot name="edit-controls" :actions="{ saveCamera, saveSlideAs }">
                <VButton class="mr-4" size="xs" @click="saveCamera">Save</VButton>
                <VButton size="xs" @click="cleanupAndLeave">Cancel</VButton>
              </slot>
            </div>
            <div v-else-if="isEditing" class="flex flex-none ml-auto gap-2">
              <slot name="edit-controls" :actions="{ saveCamera, saveSlideAs }">
                <VButton class="mr-4" size="xs" data-testid="save-btn" @click="saveCamera">Save</VButton>
                <VButton key="swap" size="xs" data-testid="save-as-btn" @click="saveSlideAs">Save As</VButton>
              </slot>
            </div>
          </EditorTitleOverlay>
          <slot name="message-banner">
            <div class="p-4">
              <VMessage
                class="items-center"
                :show="isEditing && isNew && !hideMessage"
                banner-type="info"
                data-testid="set-scene-banner"
                @dismiss="hideMessage = true"
              >
                Click and drag to set a camera angle
              </VMessage>
            </div>
          </slot>
        </div>
      </template>
    </DeckSiteSlide>

    <Portal v-if="isEditing" to="editor-details">
      <div
        class="flex flex-col flex-grow overflow-y-auto text-white bg-gray-800"
        tabindex="0"
        @focus="setFocused(true)"
        @blur="setFocused(false)"
      >
        <EditorDeckSlidesSearchableHotspotList
          class="flex-grow"
          :hotspots="hotspots"
          :selected-hotspots="selectedLayers"
          :hidden-hotspot-ids="hiddenHotspotIds"
          :go-to-site="goToSite"
          @contextmenu="openHotspotListContextMenu"
          @click="handleLayerClick"
          @set-hotspot-visibility="onSetHotspotVisibility"
        />
      </div>
    </Portal>
    <EditorDeckModalsLeaveEditModal
      v-if="showLeaveEditModal"
      :showing="showLeaveEditModal"
      @cancel="showLeaveEditModal = false"
      @leave="cleanupAndLeave"
    />
  </div>
</template>

<script lang="ts" setup>
import { CubeIcon } from '@heroicons/vue/24/solid'
import { CubeTransparentIcon, ChevronLeftIcon } from '@heroicons/vue/24/outline'

import type { PropType, DeepReadonly } from 'vue'
import { ref, watch, onUnmounted, toRefs } from 'vue'

import type { SiteState } from '@/composables/useDeck'
import type { SiteCamera as Camera } from '@/entities/siteCamera'
import type { Hotspot } from '@/entities/site'
import type { ThreeDElement, ThreeDVisibleElement } from '@/composables/useLayerSelect'
import type { UseThreeDViewer } from '@/composables/useThreeDViewer'
import type { Site, SiteCamera } from '@/entities/site'
import type { SiteSlide as SiteSlideType } from '@/entities/deck'

import { uvToCameraCoords } from '@/lib/site-viewer/utils'
import { selectableId } from '@/lib/three-d-editor/utils'

type PanCoords = {
  pan_x: number
  pan_y: number
}

const props = defineProps({
  site: {
    type: Object as PropType<DeepReadonly<SiteState>>,
    required: true,
  },
  siteData: {
    type: Object as PropType<Site | null>,
    default: null,
  },
  cameraId: {
    type: Number as PropType<number | null>,
    default: null,
  },
  slideRefreshKey: {
    type: Number,
    default: 0,
  },
  slide: {
    type: Object as PropType<DeepReadonly<SiteSlideType>>,
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  isNew: {
    type: Boolean,
    default: false,
  },
  pageLocked: {
    type: Boolean,
    default: false,
  },
  threeDViewer: {
    type: Object as PropType<UseThreeDViewer>,
    required: true,
  },
})

const emit = defineEmits([
  'update:slide',
  'create-slide',
  'finish-slide',
  'camera-ready',
  'start-edit',
  'leave-edit-and-reset',
  'contextmenu',
  'change',
  'update:siteData',
])

const { vpeUrl } = useVpeUrl()
const slideTitle = ref(props.slide.title)
const slideTitleRef = ref<any | null>(null)
const cameraChanges = ref<Partial<SiteCamera> | null>(null)
const initialPanCoords = ref<PanCoords | null>(null)
const hideMessage = ref(false)
const showLeaveEditModal = ref(false)
const camera = ref<Camera | null>(null)
const initialized = ref(false)
const { state: userState } = useUser()
const { slide, slideRefreshKey, pageLocked } = toRefs(props)
const hiddenHotspotIds = ref<(number | undefined)[]>([])
const hotspots = ref<Hotspot[]>([])
const clickedHotspot = ref<Hotspot | null>(null)
const siteEditorRefreshKey = ref(0) // Used to handle creation from null cameras

watch(slide, newSlide => {
  slideTitle.value = newSlide.title
  hideMessage.value = false
  initialized.value = false
  clearLayerSelection()
})

watch(
  () => props.siteData,
  newSiteData => {
    if (newSiteData) {
      hotspots.value = newSiteData.hotspots
    }
  },
)

watch(slideRefreshKey, key => {
  const isCreatingNewCameraFromNullCamera = props.isNew && !props.cameraId
  if (isCreatingNewCameraFromNullCamera) return

  siteEditorRefreshKey.value = key
})

const { selectedLayers, setFocused, handleLayerClick, selectLayer, clearLayerSelection } = useLayerSelect<Hotspot>({
  layers: hotspots,
  getSelectableId: v => `${v.id}`,
  onLayerSelected: (item: DeepReadonly<Hotspot>) => selectHotspot(item as Hotspot),
})

function goToSite() {
  window.open(`${vpeUrl}/projects/${props.slide.project_id}/sites/${props.slide.site_id}`, '_blank')
}

// save camera for the current slide
function saveCamera() {
  if (!cameraChanges.value) return
  const site_camera = _getPayload()
  emit('update:slide', { id: props.slide.id, site_id: props.slide.site_id, site_camera, title: slideTitle.value })
  emit('finish-slide')
  cleanup()
}

// create a new slide with new camera
function saveSlideAs() {
  const site_camera = _getPayload()
  const { id, ...slideParams } = props.slide
  if (site_camera) {
    emit('create-slide', { ...slideParams, site_camera, title: slideTitle.value })
    emit('finish-slide')
    cleanup()
  } else {
    cleanupAndLeave()
  }
}

function cleanupAndLeave() {
  cleanup()
  emit('leave-edit-and-reset')
}

function cleanup() {
  showLeaveEditModal.value = false
  initialized.value = false
  clearLayerSelection()
}

function onLeave() {
  if (pageLocked.value) {
    showLeaveEditModal.value = true
  } else {
    cleanupAndLeave()
  }
}

function _getPayload(): Partial<SiteCamera> | null {
  if (!props.site.siteId || !props.siteData) return null
  let panCoords: PanCoords | null

  if (cameraChanges.value?.coordinate) {
    panCoords = uvToCameraCoords(cameraChanges.value?.coordinate)
  } else if (props.slide.camera_id) {
    panCoords = initialPanCoords.value
  } else {
    panCoords = uvToCameraCoords({ x: props.siteData.initial_pan_x, y: props.siteData.initial_pan_y })
  }

  let payload: Partial<SiteCamera> = { site_id: props.site.siteId, camera_id: props.site.cameraId, ...panCoords }

  if (cameraChanges.value?.zoom) {
    payload.zoom = cameraChanges.value.zoom
  }

  if (hiddenHotspotIds.value) {
    payload.hidden_hotspot_ids = hiddenHotspotIds.value
  }

  return payload
}

onUnmounted(() => {
  emit('finish-slide')
})

function onSetHotspotVisibility(e: any) {
  if (e.visible) {
    hiddenHotspotIds.value = hiddenHotspotIds.value.filter(h => h !== e.hotspotId)
  } else {
    hiddenHotspotIds.value = Array.from(new Set([...hiddenHotspotIds.value, e.hotspotId]))
  }
  emit('change')
}

function openHotspotListContextMenu(e: MouseEvent, hotspotOrHotspots: Hotspot | Hotspot[]): void {
  if (!props.isEditing) return
  if (!Array.isArray(hotspotOrHotspots)) {
    return openHotspotListContextMenu(e, [hotspotOrHotspots])
  }

  const hotspots = hotspotOrHotspots

  emit('contextmenu', {
    event: e,
    context: hotspots,
    options: [
      {
        label: 'Hide/Show',
        call: () => {
          const selectedIds = hotspots.map(h => h.id)
          // Set all hidden if _any_ are visible, or visible if _all_ are hidden.
          const visible = !selectedIds.some(id => {
            return !hiddenHotspotIds.value.includes(id)
          })

          if (visible) {
            hiddenHotspotIds.value = hiddenHotspotIds.value.filter(id => (id ? !selectedIds.includes(id) : null))
          } else {
            hiddenHotspotIds.value = [...hiddenHotspotIds.value, ...selectedIds]
          }
          emit('change')
        },
      },
    ].map((obj, id) => ({ ...obj, id })),
  })
}

function selectHotspot(hotspot: Hotspot) {
  emit('change')
  clickedHotspot.value = hotspot
}

function onInteraction() {
  if (initialized.value) {
    emit('change')
  }
}

function updateCamera(e: { event: string; payload: any }) {
  cameraChanges.value = { ...cameraChanges.value, ...e.payload }
}

function initCameraParams(e: Camera) {
  if (e) {
    initialPanCoords.value = { pan_x: e.pan_x as number, pan_y: e.pan_y as number }
    hiddenHotspotIds.value = e.hidden_hotspots.map(hs => hs.hotspot_id)
    camera.value = e
  }
}

function saveTitle() {
  if (props.isEditing) return
  emit('update:slide', { id: slide.value.id, site_id: slide.value.site_id, title: slideTitle.value })
}
</script>
